import { Effect, Schema } from 'effect';
import { OPIN_PARTICIPANT_ROLES } from '@finnest/regulatory-vocabulary';
import type { OpinParticipantRole as VocabularyParticipantRole } from '@finnest/regulatory-vocabulary';

export { OPIN_PARTICIPANT_ROLES };

/**
 * Schema projection of the canonical OPIN participant-role vocabulary.
 *
 * `@finnest/regulatory-vocabulary` declares the member list and sits below `@finnest/core`
 * (and below effect), so the schema is built here — the first layer that owns both. Everything
 * downstream imports the schema, never the bare tuple, so an operator-stated role is parsed
 * rather than asserted.
 */
export const OpinParticipantRole = Schema.Literals(OPIN_PARTICIPANT_ROLES);
export type OpinParticipantRole = typeof OpinParticipantRole.Type;

/**
 * The schema projection is the vocabulary, asserted rather than assumed: if
 * `@finnest/regulatory-vocabulary` gains or drops a member without this file changing, the
 * build breaks here instead of in a consumer that silently stopped covering a role.
 */
type ParticipantRoleOutsideSchema = Exclude<VocabularyParticipantRole, OpinParticipantRole>;
type SchemaOutsideParticipantRole = Exclude<OpinParticipantRole, VocabularyParticipantRole>;
type SchemaIsVocabulary = [ParticipantRoleOutsideSchema, SchemaOutsideParticipantRole] extends [
  never,
  never,
]
  ? true
  : [ParticipantRoleOutsideSchema, SchemaOutsideParticipantRole];
export const OPIN_PARTICIPANT_ROLE_SCHEMA_IS_VOCABULARY: SchemaIsVocabulary = true;

/**
 * The role a deployment runs as when no operator has stated one.
 *
 * This is a genuine product default (a Finnest deployment publishes provider APIs unless the
 * operator says otherwise), not a fallback for unparseable input: an unrecognised value is
 * still a parse failure. It is stated once, here, and reached only through
 * {@link StatedOpinParticipantRole}.
 */
export const DEFAULT_OPIN_PARTICIPANT_ROLE: OpinParticipantRole = 'transmitter';

/**
 * The single boundary parse for an operator-stated (or unstated) participant role.
 *
 * Every place that used to write `role ?? 'transmitter'` parses through this instead, so the
 * default exists in one schema and a typo is rejected rather than silently promoted to
 * `transmitter` — which is what the deleted `resolveParticipantRole`/
 * `opinParticipantRoleOrDefault` helpers did.
 */
export const StatedOpinParticipantRole = OpinParticipantRole.pipe(
  Schema.withDecodingDefault(Effect.succeed(DEFAULT_OPIN_PARTICIPANT_ROLE)),
);

/**
 * A stated `[opin]` block, decoded into the deployment's complete OPIN posture.
 *
 * The `[opin]` block is optional everywhere it appears — the finnest.toml block, the runtime
 * deploy slice, the pulumi runtime config — and it stays optional, because the TOML writer
 * round-trips an operator's file key for key and an unset key must not be invented. An absent
 * block and a block that states no role have always meant the same thing (`readOpin` parses
 * `overlay ?? {}`), so both decode here into the single stated product default.
 *
 * Untyped input decodes the whole block through this schema (default decoder: keys this schema
 * does not name are dropped — each block's own schema owns them). Already-typed input goes
 * through {@link statedOpinParticipantRole}. Both read the one {@link DEFAULT_OPIN_PARTICIPANT_ROLE}.
 */
export const StatedOpinBlock = Schema.Struct({ participantRole: StatedOpinParticipantRole });
export type StatedOpinBlock = typeof StatedOpinBlock.Type;

/**
 * The deployment's participant role from its stated (or absent) `[opin]` block. The block is
 * already typed, so the only decision left is the product default for an unstated role.
 */
export function statedOpinParticipantRole(
  block: typeof StatedOpinBlock.Encoded | undefined,
): OpinParticipantRole {
  return block?.participantRole ?? DEFAULT_OPIN_PARTICIPANT_ROLE;
}

/**
 * A platform slice's stated (or absent) `${cloud}:opinParticipantRole`, decoded to the role.
 *
 * The slice field stays optional — a deploy intent emitted before the key existed must still
 * parse, and the emitted JSON shape is a wire contract — so the absent case needs exactly one
 * meaning. `infra/pulumi/contract` used to declare a second `DEFAULT_OPIN_PARTICIPANT_ROLE` for
 * it, which both platform programs then reached with `?? DEFAULT`: two constants that had to
 * agree by inspection, feeding `runtimeSecretSeed.role` and therefore which regulated material
 * a deploy seeds. There is one constant now, and this is its platform-slice boundary.
 */
export const StatedPlatformOpinRoleSlice = Schema.Struct({
  opinParticipantRole: StatedOpinParticipantRole,
});
export type StatedPlatformOpinRoleSlice = typeof StatedPlatformOpinRoleSlice.Type;

export function statedPlatformOpinParticipantRole(
  slice: typeof StatedPlatformOpinRoleSlice.Encoded | undefined,
): OpinParticipantRole {
  return slice?.opinParticipantRole ?? DEFAULT_OPIN_PARTICIPANT_ROLE;
}
